/**
 * Dashboard API utilities for real-time data fetching
 * Used by dashboard components for live monitoring
 */

const DEFAULT_TIMEOUT = 10000 // 10 seconds

/**
 * Fetch with timeout support
 */
const fetchWithTimeout = async (url, options = {}) => {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options
  
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)
  
  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return response
  } catch (error) {
    clearTimeout(timeoutId)
    
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timed out')
      timeoutError.code = 'TIMEOUT'
      throw timeoutError
    }
    
    throw error
  }
}

/**
 * Fetch current faults
 * Returns array of active fault conditions
 */
export const fetchFaults = async () => {
  try {
    const response = await fetchWithTimeout('/faults')
    const data = await response.json()
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Failed to fetch faults:', error)
    throw error
  }
}

/**
 * Fetch combined dashboard data (real-time data + faults)
 * Returns object with { realTimeData: [], faults: [] }
 * This replaces separate calls to fetchRealTimeData() and fetchFaults()
 */
export const fetchDashboardData = async () => {
  try {
    const response = await fetchWithTimeout('/api/dashboard-combined')
    const data = await response.json()
    return {
      realTimeData: Array.isArray(data.realTimeData) ? data.realTimeData : [],
      faults: Array.isArray(data.faults) ? data.faults : [],
    }
  } catch (error) {
    console.error('Failed to fetch dashboard data:', error)
    throw error
  }
}

/**
 * Fetch available dashboard fields
 * Returns array of device/measurement combinations available for charting
 */
export const fetchDashboardFields = async () => {
  try {
    const response = await fetchWithTimeout('/api/dashboard-fields')
    const data = await response.json()
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Failed to fetch dashboard fields:', error)
    throw error
  }
}

/**
 * Get historical data for a specific device/measurement (used for chart initialization)
 * @param {string} device - Device name (e.g., 'bat1', 'alt1')
 * @param {string} measurement - Measurement type (e.g., 'volts', 'amps')
 * @param {number} seconds - Number of seconds of history to fetch (default: 30)
 */
export const fetchHistoricalData = async (device, measurement, seconds = 30) => {
  try {
    const end = new Date()
    const start = new Date(end.getTime() - (seconds * 1000))
    
    const params = new URLSearchParams({
      device,
      measurement,
      start: start.toISOString(),
      end: end.toISOString(),
    })
    
    const response = await fetchWithTimeout(`/api/historical?${params}`)
    const data = await response.json()
    return Array.isArray(data) ? data : []
  } catch (error) {
    console.error(`Failed to fetch historical data for ${device}-${measurement}:`, error)
    throw error
  }
}
